import type { PartTargetedRecord, PartType, ServiceRecord } from '../../types';

// Single source of truth for the reset derivation: effective usage is the raw counter
// reading minus the snapshot taken by the most recent active replaced/serviced record
// targeting the counter, plus that record's initialUsage when the replacement was a
// used part (SR-23). Only the latest snapshot applies — snapshots never sum.
// `unitNumber` identifies a per-unit counter (1-based); omit it for aggregate counters.
export const getEffectiveUsage = ({
    partType,
    rawUsage,
    records,
    unitNumber
}: {
    partType: PartType;
    rawUsage: number;
    records: ServiceRecord[];
    unitNumber?: number;
}): number => {
    const latestReset = records.reduce<PartTargetedRecord | null>((latest, record) => {
        if (record.voided) {
            return latest;
        }

        if (record.eventType !== 'replaced' && record.eventType !== 'serviced') {
            return latest;
        }

        if (record.partType !== partType) {
            return latest;
        }

        // A record without a unit number targets every unit of the part.
        const targetsUnit =
            unitNumber === undefined ||
            record.unitNumber === undefined ||
            record.unitNumber === unitNumber;

        if (!targetsUnit) {
            return latest;
        }

        if (!latest) {
            return record;
        }

        const isNewer =
            new Date(record.performedAt).getTime() > new Date(latest.performedAt).getTime();

        return isNewer ? record : latest;
    }, null);

    const usageSinceReset = Math.max(0, rawUsage - (latestReset?.usageAtRecord ?? 0));

    return usageSinceReset + (latestReset?.initialUsage ?? 0);
};
